/**
 * Image credits: collects figcaption (preferred) or trimmed alt from article/product images,
 * plus per-item blog gallery credits from [data-blog-gallery-item][data-credit].
 * Hero/RTE lines must contain "credit" (case-insensitive) so default Shopify alts and
 * unrelated logos are excluded. Gallery metaobject captions are already credit text and
 * are included whenever non-empty (no keyword required). Skips empty strings and the
 * Pipeline hero fallback "Banner Image".
 * Pipeline product media (media.liquid) only outputs the first segment of alt when # and _ are used for variant filters.
 * @version 2026-07-30-gallery-credits
 */
(function () {
  'use strict';

  var IGNORED_ALTS = ['banner image'];

  function normalizeCreditText(text) {
    if (!text || typeof text !== 'string') return '';
    return text.replace(/\s+/g, ' ').trim();
  }

  function containsCreditKeyword(text) {
    return normalizeCreditText(text).toLowerCase().indexOf('credit') !== -1;
  }

  function shouldSkipCredit(text, options) {
    var opts = options || {};
    var t = normalizeCreditText(text).toLowerCase();
    if (!t) return true;
    if (IGNORED_ALTS.indexOf(t) !== -1) return true;
    if (!opts.skipKeywordCheck && !containsCreditKeyword(text)) return true;
    return false;
  }

  function creditForImage(img) {
    if (!img || img.tagName !== 'IMG') return '';
    var figure = img.closest('figure');
    if (figure) {
      var cap = figure.querySelector('figcaption');
      if (cap) {
        var fromCap = normalizeCreditText(cap.textContent);
        if (fromCap) return fromCap;
      }
    }
    return normalizeCreditText(img.getAttribute('alt') || '');
  }

  function gatherBlogGallery(root) {
    var texts = [];
    root.querySelectorAll('[data-blog-gallery-item][data-credit]').forEach(function (item) {
      var c = normalizeCreditText(item.getAttribute('data-credit') || '');
      // Gallery captions from the blog_gallery_image metaobject are already credits;
      // do not require the literal word "credit".
      if (!shouldSkipCredit(c, { skipKeywordCheck: true })) texts.push(c);
    });
    return texts;
  }

  function gatherArticle(root, includeFeatured, includeRte) {
    var texts = [];
    if (includeFeatured) {
      root.querySelectorAll('[data-overlay-header] img').forEach(function (img) {
        var c = creditForImage(img);
        if (!shouldSkipCredit(c)) texts.push(c);
      });
    }
    // Always include blog image gallery per-item credits on article pages.
    gatherBlogGallery(root).forEach(function (c) {
      texts.push(c);
    });
    if (includeRte) {
      root.querySelectorAll('.blog__article__content.rte img').forEach(function (img) {
        var c = creditForImage(img);
        if (!shouldSkipCredit(c)) texts.push(c);
      });
    }
    return texts;
  }

  function gatherProduct(root, includeGallery, includeRte) {
    var texts = [];
    if (includeGallery) {
      var seen = new Set();
      root.querySelectorAll('[data-product-slideshow] img, .product__media img').forEach(function (img) {
        var c = creditForImage(img);
        if (shouldSkipCredit(c)) return;
        var key = c.toLowerCase();
        if (seen.has(key)) return;
        seen.add(key);
        texts.push(c);
      });
    }
    // Blog/product metafield galleries share the same markup and credit attributes.
    gatherBlogGallery(root).forEach(function (c) {
      texts.push(c);
    });
    if (includeRte) {
      root
        .querySelectorAll('.product__block__description.rte img, .product-description__content img')
        .forEach(function (img) {
          var c = creditForImage(img);
          if (!shouldSkipCredit(c)) texts.push(c);
        });
    }
    return texts;
  }

  function dedupeOrdered(list) {
    var seen = new Set();
    var out = [];
    list.forEach(function (item) {
      var key = item.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      out.push(item);
    });
    return out;
  }

  function parseBool(value) {
    return value === true || value === 'true';
  }

  function initSection(sectionRoot) {
    if (!sectionRoot || sectionRoot.getAttribute('data-image-credits-initialized') === 'true') return;

    var pageType = sectionRoot.getAttribute('data-page-type');
    var includeFeatured = parseBool(sectionRoot.getAttribute('data-include-featured'));
    var includeGallery = parseBool(sectionRoot.getAttribute('data-include-gallery'));
    var includeRte = parseBool(sectionRoot.getAttribute('data-include-rte'));

    var collected =
      pageType === 'article'
        ? gatherArticle(document, includeFeatured, includeRte)
        : gatherProduct(document, includeGallery, includeRte);

    var credits = dedupeOrdered(collected);

    sectionRoot.classList.remove('image-credits--pending');

    if (credits.length === 0) {
      sectionRoot.setAttribute('hidden', '');
      sectionRoot.classList.add('image-credits--empty');
      return;
    }

    var shell = sectionRoot.querySelector('[data-image-credits-shell]');
    var listEl = sectionRoot.querySelector('[data-image-credits-list]');
    var toggle = sectionRoot.querySelector('[data-image-credits-toggle]');
    var panel = sectionRoot.querySelector('[data-image-credits-panel]');

    if (!shell || !listEl || !toggle || !panel) return;

    listEl.innerHTML = '';
    credits.forEach(function (text) {
      var li = document.createElement('li');
      li.className = 'image-credits__item';
      li.textContent = text;
      listEl.appendChild(li);
    });

    shell.removeAttribute('hidden');
    sectionRoot.setAttribute('data-image-credits-initialized', 'true');

    var reduceMotion =
      typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function setOpen(open) {
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (open) {
        panel.removeAttribute('hidden');
        sectionRoot.classList.add('image-credits--open');
      } else {
        panel.setAttribute('hidden', '');
        sectionRoot.classList.remove('image-credits--open');
      }
      if (reduceMotion) {
        panel.style.transition = 'none';
      }
    }

    setOpen(false);

    toggle.addEventListener('click', function () {
      var isOpen = toggle.getAttribute('aria-expanded') === 'true';
      setOpen(!isOpen);
    });
  }

  function initAll() {
    document.querySelectorAll('[data-image-credits]:not([data-image-credits-initialized])').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    var target = event.target;
    if (!target) return;
    var root =
      target.matches && target.matches('[data-image-credits]')
        ? target
        : target.querySelector && target.querySelector('[data-image-credits]');
    if (root) {
      root.removeAttribute('data-image-credits-initialized');
      initSection(root);
    }
  });
})();
